import { useState, useEffect } from "react";
import { fetchJobseekerMetrics } from "../services/jobService";
import { getToken } from "../services/authService";
import StudentHeader from "../components/student/StudentHeader";

export default function Dashboard() {
  const [windowDays, setWindowDays] = useState(30);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth : 1280
  );

  const isTablet = viewportWidth <= 1024;
  const isMobile = viewportWidth <= 768;
  const isSmallMobile = viewportWidth <= 520;

  // Get email from localStorage or token
  const getEmail = () => {
    const profileData = localStorage.getItem("profileData");
    if (profileData) {
      try {
        const parsed = JSON.parse(profileData);
        return parsed.basicInfo?.email;
      } catch (e) {
        console.error("Error parsing profileData:", e);
      }
    }
    const token = getToken();
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split(".")[1]));
        return payload.sub || payload.email;
      } catch (e) {
        // Error parsing token
      }
    }
    return null;
  };

  // Fetch metrics when windowDays changes
  useEffect(() => {
    const loadMetrics = async () => {
      const email = getEmail();
      if (!email) {
        setError("Email not found. Please login again.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const data = await fetchJobseekerMetrics(email, windowDays);
        setMetrics(data);
      } catch (err) {
        console.error("Error fetching metrics:", err);
        setError(err.message || "Failed to load metrics. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    loadMetrics();
  }, [windowDays]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const styles = {
    page: {
      background: "#f9fafb",
      minHeight: "100vh",
    },
    container: {
      maxWidth: "1400px",
      margin: "0 auto",
      padding: "32px 40px",
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "32px",
    },
    title: {
      fontSize: "28px",
      fontWeight: 600,
      color: "#111827",
      margin: 0,
    },
    dropdownContainer: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
    },
    dropdownLabel: {
      fontSize: "14px",
      color: "#374151",
      fontWeight: 500,
    },
    dropdown: {
      padding: "8px 12px",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
      fontSize: "14px",
      background: "#ffffff",
      cursor: "pointer",
      outline: "none",
      minWidth: "120px",
    },
    loading: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#6b7280",
      fontSize: "16px",
    },
    error: {
      textAlign: "center",
      padding: "60px 20px",
      color: "#ef4444",
      fontSize: "16px",
      background: "#fff1f2",
      borderRadius: "8px",
      border: "1px solid #ef4444",
      margin: "20px 0",
    },
    metricsContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "24px",
      marginTop: "24px",
    },
    metricCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "28px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)",
      border: "1px solid rgba(0, 0, 0, 0.04)",
      transition: "all 0.3s ease",
    },
    metricTitle: {
      fontSize: "14px",
      color: "#6b7280",
      fontWeight: 500,
      marginBottom: "8px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    metricValue: {
      fontSize: "32px",
      fontWeight: 600,
      color: "#111827",
      marginBottom: "8px",
    },
    metricDescription: {
      fontSize: "14px",
      color: "#6b7280",
    },
    section: {
      marginTop: "40px",
    },
    sectionTitle: {
      fontSize: "20px",
      fontWeight: 600,
      color: "#111827",
      marginBottom: "24px",
    },
    chartsContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
      gap: "16px",
    },
    statusCard: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "24px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)",
      border: "1px solid rgba(0, 0, 0, 0.04)",
      transition: "all 0.3s ease",
    },
    statusHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "12px",
    },
    statusName: {
      fontSize: "14px",
      fontWeight: 500,
      color: "#374151",
      textTransform: "capitalize",
    },
    statusCount: {
      fontSize: "18px",
      fontWeight: 600,
      color: "#111827",
    },
    barContainer: {
      width: "100%",
      height: "24px",
      background: "#f3f4f6",
      borderRadius: "12px",
      overflow: "hidden",
      position: "relative",
    },
    bar: {
      height: "100%",
      borderRadius: "12px",
      transition: "width 0.3s ease",
    },
    srOnly: {
      position: "absolute",
      width: "1px",
      height: "1px",
      padding: 0,
      margin: "-1px",
      overflow: "hidden",
      clip: "rect(0, 0, 0, 0)",
      whiteSpace: "nowrap",
      border: 0,
    },
  };

  // Helper function to get color for each status
  const getStatusColor = (status) => {
    const colors = {
      APPLIED: "#2d6a5e",
      UNDER_REVIEW: "#f59e0b", // Amber
      INTERVIEW: "#8b5cf6", // Purple
      OFFERED: "#10b981", // Green
      HIRED: "#059669", // Dark Green
      REJECTED: "#ef4444", // Red
    };
    return colors[status] || "#6b7280"; // Default gray
  };

  return (
    <div style={styles.page}>
      <StudentHeader activePage="dashboard" />

      <main
        style={{
          ...styles.container,
          ...(isTablet ? { padding: "24px 24px" } : {}),
          ...(isMobile ? { padding: "20px 16px" } : {}),
        }}
        aria-label="Student dashboard"
      >
        <div
          style={{
            ...styles.header,
            ...(isMobile
              ? {
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: "12px",
                  marginBottom: "20px",
                }
              : {}),
          }}
        >
          <h1 style={styles.title}>Dashboard</h1>
          <div
            style={{
              ...styles.dropdownContainer,
              ...(isMobile ? { flexDirection: "column", alignItems: "stretch", gap: "6px" } : {}),
            }}
          >
            <label htmlFor="dashboard-window-days" style={styles.dropdownLabel}>
              Time Period
            </label>
            <select
              id="dashboard-window-days"
              style={{
                ...styles.dropdown,
                ...(isMobile ? { width: "100%" } : {}),
              }}
              value={windowDays}
              onChange={(e) => setWindowDays(Number(e.target.value))}
            >
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
              <option value={90}>Last 90 days</option>
              <option value={365}>Last 365 days</option>
            </select>
          </div>
        </div>

        {loading && (
          <div style={styles.loading} role="status" aria-live="polite">
            Loading metrics...
          </div>
        )}

        {error && (
          <div style={styles.error} role="alert">
            {error}
          </div>
        )}

        {!loading && !error && metrics && (
          <>
            {/* Summary Cards */}
            <section
              style={{
                ...styles.metricsContainer,
                ...(isTablet ? { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" } : {}),
                ...(isMobile ? { gridTemplateColumns: "1fr", gap: "16px", marginTop: "18px" } : {}),
              }}
              aria-labelledby="student-dashboard-summary"
            >
              <h2 id="student-dashboard-summary" style={styles.srOnly}>
                Application summary
              </h2>
              <article
                style={{
                  ...styles.metricCard,
                  ...(isSmallMobile ? { padding: "20px" } : {}),
                }}
              >
                <div style={styles.metricTitle}>Total Applications</div>
                <div style={styles.metricValue}>{metrics.totalApplied || 0}</div>
                <div style={styles.metricDescription}>All time applications</div>
              </article>
              <article
                style={{
                  ...styles.metricCard,
                  ...(isSmallMobile ? { padding: "20px" } : {}),
                }}
              >
                <div style={styles.metricTitle}>Applications in Period</div>
                <div style={styles.metricValue}>{metrics.appliedInWindow || 0}</div>
                <div style={styles.metricDescription}>Last {windowDays} days</div>
              </article>
            </section>

            {/* Status Breakdown - All Time */}
            <section style={styles.section} aria-labelledby="student-dashboard-all-time-title">
              <h2 id="student-dashboard-all-time-title" style={styles.sectionTitle}>
                Status Breakdown (All Time)
              </h2>
              <div
                style={{
                  ...styles.chartsContainer,
                  ...(isMobile ? { gridTemplateColumns: "1fr", gap: "12px" } : {}),
                }}
              >
                {Object.entries(metrics.totalByStatus || {}).map(([status, count]) => (
                  <article
                    key={status}
                    style={{
                      ...styles.statusCard,
                      ...(isSmallMobile ? { padding: "18px" } : {}),
                    }}
                  >
                    <div style={styles.statusHeader}>
                      <div style={styles.statusName}>{status.replace(/_/g, ' ')}</div>
                      <div style={styles.statusCount}>{count}</div>
                    </div>
                    <div style={styles.barContainer}>
                      <div
                        style={{
                          ...styles.bar,
                          width: `${metrics.totalApplied > 0 ? (count / metrics.totalApplied) * 100 : 0}%`,
                          background: getStatusColor(status),
                        }}
                        aria-hidden="true"
                      />
                    </div>
                    <span style={styles.srOnly}>
                      {status.replace(/_/g, " ")}: {count} of {metrics.totalApplied || 0}
                    </span>
                  </article>
                ))}
              </div>
            </section>

            {/* Status Breakdown - In Window */}
            <section style={styles.section} aria-labelledby="student-dashboard-window-title">
              <h2 id="student-dashboard-window-title" style={styles.sectionTitle}>
                Status Breakdown (Last {windowDays} Days)
              </h2>
              <div
                style={{
                  ...styles.chartsContainer,
                  ...(isMobile ? { gridTemplateColumns: "1fr", gap: "12px" } : {}),
                }}
              >
                {Object.entries(metrics.byStatusInWindow || {}).map(([status, count]) => (
                  <article
                    key={status}
                    style={{
                      ...styles.statusCard,
                      ...(isSmallMobile ? { padding: "18px" } : {}),
                    }}
                  >
                    <div style={styles.statusHeader}>
                      <div style={styles.statusName}>{status.replace(/_/g, ' ')}</div>
                      <div style={styles.statusCount}>{count}</div>
                    </div>
                    <div style={styles.barContainer}>
                      <div
                        style={{
                          ...styles.bar,
                          width: `${metrics.appliedInWindow > 0 ? (count / metrics.appliedInWindow) * 100 : 0}%`,
                          background: getStatusColor(status),
                        }}
                        aria-hidden="true"
                      />
                    </div>
                    <span style={styles.srOnly}>
                      {status.replace(/_/g, " ")}: {count} of {metrics.appliedInWindow || 0}
                    </span>
                  </article>
                ))}
              </div>
            </section>
          </>
        )}

        {!loading && !error && !metrics && (
          <div style={styles.loading} role="status">
            No metrics data available
          </div>
        )}
      </main>
    </div>
  );
}
